// Shared validation utilities for diagnostic tests

/**
 * Validate that an object has required fields
 * @param {Object} obj - Object to validate
 * @param {Array<string>} requiredFields - Field names that must exist
 * @returns {{valid: boolean, missing: Array<string>}}
 */
export const validateSchema = (obj, requiredFields) => {
  if (!obj || typeof obj !== 'object') {
    return { valid: false, missing: requiredFields };
  }

  const missing = requiredFields.filter(field => {
    const value = obj[field];
    return value === undefined || value === null;
  });

  return { valid: missing.length === 0, missing };
};

/**
 * Validate that an array of objects all have required fields
 * @param {Array} items - Array of objects to validate
 * @param {Array<string>} requiredFields - Field names that must exist
 * @param {string} itemName - Name for error messages (e.g., "users", "cards")
 * @returns {{valid: boolean, errors: Array<string>, validCount: number, invalidCount: number}}
 */
export const validateArraySchema = (items, requiredFields, itemName = 'items') => {
  if (!Array.isArray(items)) {
    return { valid: false, errors: [`${itemName} is not an array`], validCount: 0, invalidCount: 0 };
  }

  const errors = [];
  let validCount = 0;
  let invalidCount = 0;

  items.forEach((item, index) => {
    const result = validateSchema(item, requiredFields);
    if (result.valid) {
      validCount++;
    } else {
      invalidCount++;
      if (errors.length < 5) { // Limit error messages
        const id = item?.id || item?.uid || `index ${index}`;
        errors.push(`${itemName}[${id}] missing: ${result.missing.join(', ')}`);
      }
    }
  });

  if (invalidCount > 5) {
    errors.push(`...and ${invalidCount - 5} more invalid ${itemName}`);
  }

  return { valid: invalidCount === 0, errors, validCount, invalidCount };
};

/**
 * Check referential integrity - verify all IDs in one collection exist in another
 * @param {Array} sourceItems - Items with references
 * @param {Array} targetItems - Items being referenced
 * @param {string} sourceField - Field name in source containing the reference
 * @param {string} targetField - Field name in target to match against (default: 'id')
 * @returns {{valid: boolean, orphans: Array, orphanCount: number}}
 */
export const checkReferentialIntegrity = (sourceItems, targetItems, sourceField, targetField = 'id') => {
  const targetIds = new Set(targetItems.map(item => item[targetField]));
  const orphans = [];

  sourceItems.forEach(item => {
    const refValue = item[sourceField];
    if (refValue && !targetIds.has(refValue)) {
      if (orphans.length < 10) {
        orphans.push({
          itemId: item.id || item.uid,
          missingRef: refValue,
          field: sourceField,
        });
      }
    }
  });

  return {
    valid: orphans.length === 0,
    orphans,
    orphanCount: sourceItems.filter(item => item[sourceField] && !targetIds.has(item[sourceField])).length,
  };
};

/**
 * Find duplicate values in an array of objects
 * @param {Array} items - Array of objects
 * @param {string} field - Field to check for duplicates
 * @returns {{hasDuplicates: boolean, duplicates: Array<{value: any, count: number}>}}
 */
export const findDuplicates = (items, field) => {
  const counts = new Map();

  items.forEach(item => {
    const value = item[field];
    if (value !== undefined && value !== null) {
      counts.set(value, (counts.get(value) || 0) + 1);
    }
  });

  const duplicates = [];
  counts.forEach((count, value) => {
    if (count > 1) {
      duplicates.push({ value, count });
    }
  });

  return { hasDuplicates: duplicates.length > 0, duplicates };
};

/**
 * Dry-run validation for addReceipt
 * @param {Object} receiptData - Receipt data to validate
 * @param {Object} context - { workOrders, canWrite }
 * @returns {{valid: boolean, status: string, message: string}}
 */
export const dryRunAddReceipt = (receiptData, context) => {
  // Check permissions
  if (!context.canWrite || !context.canWrite()) {
    return { valid: false, status: 'fail', message: 'No write access (demo user)' };
  }

  // Validate required fields
  const required = ['woId'];
  const { valid, missing } = validateSchema(receiptData, required);
  if (!valid) {
    return { valid: false, status: 'fail', message: `Missing required fields: ${missing.join(', ')}` };
  }

  // Check WO reference exists
  if (context.workOrders) {
    const woExists = context.workOrders.some(wo => wo.id === receiptData.woId);
    if (!woExists) {
      return { valid: false, status: 'fail', message: `Invalid woId: ${receiptData.woId} not found` };
    }
  }

  return { valid: true, status: 'pass', message: 'Receipt would be created successfully' };
};

/**
 * Dry-run validation for addLockChange
 * @param {Object} lockChangeData - Lock change data to validate
 * @param {Object} context - { lockCodes, canWrite }
 * @returns {{valid: boolean, status: string, message: string}}
 */
export const dryRunAddLockChange = (lockChangeData, context) => {
  // Check permissions
  if (!context.canWrite || !context.canWrite()) {
    return { valid: false, status: 'fail', message: 'No write access (demo user)' };
  }

  // Validate required fields
  const required = ['unitId', 'newLock'];
  const { valid, missing } = validateSchema(lockChangeData, required);
  if (!valid) {
    return { valid: false, status: 'fail', message: `Missing required fields: ${missing.join(', ')}` };
  }

  return {
    valid: true,
    status: 'pass',
    message: 'Lock change would update both lock_changes and lock_codes collections'
  };
};

/**
 * Dry-run validation for card assignment
 * @param {string} cardLast4 - Card to assign
 * @param {Object} context - { cards, users, userProfile, canWrite }
 * @returns {{valid: boolean, status: string, message: string}}
 */
export const dryRunAssignCard = (cardLast4, context) => {
  // Check permissions
  if (!context.canWrite || !context.canWrite()) {
    return { valid: false, status: 'fail', message: 'No write access (demo user)' };
  }

  if (!context.userProfile) {
    return { valid: false, status: 'fail', message: 'Not authenticated' };
  }

  // Check if already assigned to self
  const currentAssigned = context.userProfile.assignedCards || [];
  if (currentAssigned.includes(cardLast4)) {
    return { valid: true, status: 'warn', message: 'Card already assigned to you' };
  }

  // Check if card exists
  const cardExists = context.cards?.some(c => c.cardLast4 === cardLast4);
  if (!cardExists) {
    return { valid: false, status: 'fail', message: `Card ${cardLast4} not found` };
  }

  // Check if assigned to someone else
  const otherUser = context.users?.find(u =>
    u.uid !== context.userProfile.uid &&
    (u.assignedCards || []).includes(cardLast4)
  );
  if (otherUser) {
    return {
      valid: false,
      status: 'fail',
      message: `Card already assigned to ${otherUser.displayName || otherUser.email}`
    };
  }

  return { valid: true, status: 'pass', message: 'Card would be assigned successfully' };
};

/**
 * Estimate tokens for OpenAI request (rough: ~4 chars per token)
 * @param {Array} messages - Chat messages
 * @returns {number} Estimated token count
 */
export const estimateTokens = (messages) => {
  const totalChars = messages.reduce((sum, m) => {
    if (typeof m.content === 'string') return sum + m.content.length;
    if (Array.isArray(m.content)) {
      return sum + m.content.reduce((s, c) => s + (c.text?.length || 0), 0);
    }
    return sum;
  }, 0);
  return Math.ceil(totalChars / 4);
};

/**
 * Format test duration
 * @param {number} ms - Duration in milliseconds
 * @returns {string} Formatted duration
 */
export const formatDuration = (ms) => {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60000).toFixed(1)}m`;
};

/**
 * Create a timeout promise
 * @param {number} ms - Timeout in milliseconds
 * @param {string} testName - Test name for error message
 * @returns {Promise}
 */
export const createTimeout = (ms, testName) => {
  return new Promise((_, reject) => {
    setTimeout(() => reject(new Error(`Test "${testName}" timed out after ${ms}ms`)), ms);
  });
};

/**
 * Run a test function with timeout
 * @param {Function} testFn - Test function to run
 * @param {number} timeoutMs - Timeout in milliseconds
 * @param {string} testName - Test name
 * @returns {Promise<Object>} Test result
 */
export const runWithTimeout = async (testFn, timeoutMs, testName) => {
  try {
    const result = await Promise.race([
      testFn(),
      createTimeout(timeoutMs, testName),
    ]);
    return result;
  } catch (error) {
    return {
      status: 'fail',
      message: error.message,
    };
  }
};

/**
 * Compare two collections by key field, returning missing/extra/matched items
 * @param {Array} sourceItems - Items from source of truth (e.g., API)
 * @param {Array} localItems - Items from local store (e.g., Firestore)
 * @param {string} keyField - Field to match on (e.g., 'id')
 * @returns {{missingLocally: Array, extraLocally: Array, matched: number}}
 */
export const compareCollections = (sourceItems, localItems, keyField) => {
  const sourceKeys = new Set(sourceItems.map(item => String(item[keyField])).filter(Boolean));
  const localKeys = new Set(localItems.map(item => String(item[keyField])).filter(Boolean));

  const missingLocally = [...sourceKeys].filter(k => !localKeys.has(k));
  const extraLocally = [...localKeys].filter(k => !sourceKeys.has(k));
  const matched = [...sourceKeys].filter(k => localKeys.has(k)).length;

  return { missingLocally, extraLocally, matched };
};

/**
 * Find records where specific fields differ between source and local
 * @param {Array} sourceItems - Items from source of truth
 * @param {Array} localItems - Items from local store
 * @param {string} keyField - Field to join on
 * @param {Array<string>} compareFields - Fields to compare
 * @returns {Array<{key: string, field: string, sourceValue: any, localValue: any}>}
 */
export const findFieldMismatches = (sourceItems, localItems, keyField, compareFields) => {
  const localMap = new Map();
  localItems.forEach(item => {
    const key = String(item[keyField]);
    if (key) localMap.set(key, item);
  });

  const mismatches = [];

  sourceItems.forEach(sourceItem => {
    const key = String(sourceItem[keyField]);
    const localItem = localMap.get(key);
    if (!localItem) return; // Skip missing items (handled by compareCollections)

    compareFields.forEach(field => {
      const sourceVal = String(sourceItem[field] ?? '').toLowerCase().trim();
      const localVal = String(localItem[field] ?? '').toLowerCase().trim();
      if (sourceVal !== localVal) {
        mismatches.push({
          key,
          field,
          sourceValue: sourceItem[field],
          localValue: localItem[field],
        });
      }
    });
  });

  return mismatches;
};

/**
 * Detect name variations (same person, different spellings)
 * Groups names by normalized form (lowercase, trimmed, collapsed whitespace)
 * @param {Array<string>} names - List of name strings
 * @returns {Array<{normalized: string, variations: Array<string>}>} Groups with >1 variation
 */
export const detectNameVariations = (names) => {
  const groups = new Map();

  names.filter(Boolean).forEach(name => {
    const normalized = name.toLowerCase().trim().replace(/\s+/g, ' ');
    if (!groups.has(normalized)) {
      groups.set(normalized, new Set());
    }
    groups.get(normalized).add(name);
  });

  return [...groups.entries()]
    .filter(([, variations]) => variations.size > 1)
    .map(([normalized, variations]) => ({
      normalized,
      variations: [...variations],
    }));
};

export default {
  validateSchema,
  validateArraySchema,
  checkReferentialIntegrity,
  findDuplicates,
  dryRunAddReceipt,
  dryRunAddLockChange,
  dryRunAssignCard,
  estimateTokens,
  formatDuration,
  createTimeout,
  runWithTimeout,
  compareCollections,
  findFieldMismatches,
  detectNameVariations,
};
